export interface CognitoConfig {
  userPoolId: string;
  userPoolWebClientId: string;
  oauth?: {
    domain: string;
    redirectSignIn: string;
    redirectSignOut: string;
  };
}

export interface AppConfig {
  env: 'development' | 'staging' | 'production';
  apiBaseUrl: string;
  authApiBaseUrl: string; // Separate endpoint for auth operations (OAuth, /me, etc.)
  cloudfrontUrl: string;
  cognito: CognitoConfig;
  aws: {
    region: string;
  };
  features: {
    cognito: boolean;
    analytics: boolean;
    debugging: boolean;
  };
}

const developmentConfig: AppConfig = {
  env: 'development',
  apiBaseUrl: 'https://api-dev.rocketpricing.com',
  authApiBaseUrl: 'https://auth-dev.rocketpricing.com',
  cloudfrontUrl: 'https://app-dev.rocketpricing.com',
  cognito: {
    userPoolId: 'us-east-2_VYzrD015D',
    userPoolWebClientId: '49j8hq5ajpci5gv4nfho3d5pfu',
    oauth: {
      domain: 'https://launch-dev.rocketpricing.com',
      redirectSignIn: window.location.hostname === 'localhost' ? 'https://localhost:3000/oauth/callback' : 'https://app-dev.rocketpricing.com/oauth/callback',
      redirectSignOut: window.location.hostname === 'localhost' ? 'https://localhost:3000/' : 'https://app-dev.rocketpricing.com/'
    }
  },
  aws: {
    region: 'us-east-2',
  },
  features: {
    cognito: true, // ✅ Cognito is now configured!
    analytics: true,
    debugging: true
  }
};

const stagingConfig: AppConfig = {
  env: 'staging',
  apiBaseUrl: 'https://api-stage.rocketpricing.com',
  authApiBaseUrl: 'https://auth-stage.rocketpricing.com',
  cloudfrontUrl: 'https://app-stage.rocketpricing.com',
  cognito: {
    userPoolId: 'us-east-2_WGyOVS7sr',
    userPoolWebClientId: '7knbg8r13tge3nbcr21e2qt6sl',
    oauth: {
      domain: 'https://launch-stage.rocketpricing.com',
      redirectSignIn: 'https://app-stage.rocketpricing.com/oauth/callback',
      redirectSignOut: 'https://app-stage.rocketpricing.com/'
    }
  },
  aws: {
    region: 'us-east-2',
  },
  features: {
    cognito: true,
    analytics: true,
    debugging: true
  }
};

const productionConfig: AppConfig = {
  env: 'production',
  apiBaseUrl: 'https://api.rocketpricing.com',
  authApiBaseUrl: 'https://auth.rocketpricing.com',
  cloudfrontUrl: 'https://app.rocketpricing.com',
  cognito: {
    userPoolId: 'us-east-2_Eh2IPv2z2',
    userPoolWebClientId: '2eujviqb4uap88bak965ek18tj',
    oauth: {
      domain: 'https://launch.rocketpricing.com',
      redirectSignIn: 'https://app.rocketpricing.com/oauth/callback',
      redirectSignOut: 'https://app.rocketpricing.com/'
    }
  },
  aws: {
    region: 'us-east-2',
  },
  features: {
    cognito: true,
    analytics: false, // Enable after privacy policy is implemented
    debugging: false  // NEVER enable in production
  }
};

// Determine current environment
const getEnvironment = (): AppConfig['env'] => {
  // Force development config for CloudFront dev environment
  if (import.meta.env.VITE_APP_ENV === 'staging') return 'staging';
  if (import.meta.env.VITE_APP_ENV === 'production') return 'production';
  return 'development'; // Default to development config with working Cognito
};

const configs = {
  development: developmentConfig,
  staging: stagingConfig,
  production: productionConfig,
};

const selectedEnv = getEnvironment();
const selectedConfig = configs[selectedEnv];

/**
 * Validate environment configuration
 * Warns at runtime if required configuration is missing
 */
function validateConfig(config: AppConfig): { valid: boolean; issues: string[] } {
  const issues: string[] = [];

  // Check Cognito configuration if feature is enabled
  if (config.features.cognito) {
    if (!config.cognito.userPoolId) {
      issues.push('Cognito User Pool ID is missing');
    }
    if (!config.cognito.userPoolWebClientId) {
      issues.push('Cognito App Client ID is missing');
    }
    if (!config.cognito.oauth?.domain) {
      issues.push('Cognito OAuth domain is missing');
    }
  }

  // Check API endpoints
  if (!config.apiBaseUrl) {
    issues.push('API base URL is missing');
  }
  if (!config.authApiBaseUrl) {
    issues.push('Auth API base URL is missing');
  }

  return {
    valid: issues.length === 0,
    issues
  };
}

// Validate configuration at startup
const validation = validateConfig(selectedConfig);

if (!validation.valid) {
  console.error(
    `❌ Environment configuration incomplete for ${selectedEnv}:`,
    validation.issues
  );
  console.error(
    'See frontend/react/src/config/environments.ts for setup instructions'
  );
}

// Debug logging
console.log('🔧 Environment Configuration:', {
  VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
  VITE_MODE: import.meta.env.MODE,
  selectedEnv,
  hostname: window.location.hostname,
  configValid: validation.valid,
  cognitoDomain: selectedConfig.cognito.oauth?.domain,
  cognitoEnabled: selectedConfig.features.cognito,
  apiBaseUrl: selectedConfig.apiBaseUrl,
});

export const config = selectedConfig;
export const isConfigValid = validation.valid;
export const configIssues = validation.issues;
export default config;